import { Link } from "react-router-dom";
import { TrendingUp, AlertTriangle, Mail } from "lucide-react";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-ink-950 text-white/70 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-900 flex items-center justify-center border border-gold-500/40">
                <TrendingUp className="w-5 h-5 text-gold-400" strokeWidth={2.5} />
              </div>
              <span className="font-display font-bold text-xl text-white">
                Asian<span className="gold-text">Picks</span>
              </span>
            </Link>
            <p className="mt-5 text-sm leading-relaxed max-w-md">
              Professional Asian Handicap football predictions delivered daily
              by experienced tipsters. Verified results, disciplined staking,
              transparent archive.
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-500/80">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              81.1% Verified Win Rate
            </div>

            <a
              href="https://asian-picks.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-leaderboard-banner"
              className="mt-6 block max-w-[400px] rounded-lg overflow-hidden ring-1 ring-white/10 hover:ring-gold-500/50 transition-all"
              aria-label="Asian Picks — visit our website"
            >
              <img
                src="/asian-handicap-banner-400x100.gif"
                alt="Asian Handicap — Professional Football Tips"
                width="400"
                height="100"
                loading="lazy"
                className="w-full h-auto block"
              />
            </a>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-white font-display font-semibold text-sm uppercase tracking-widest mb-5">
              Explore
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link data-testid="footer-home" to="/" className="hover:text-gold-400 transition-colors">Home</Link>
              </li>
              <li>
                <Link data-testid="footer-about" to="/about" className="hover:text-gold-400 transition-colors">About</Link>
              </li>
              <li>
                <Link data-testid="footer-predictions" to="/predictions" className="hover:text-gold-400 transition-colors">Predictions Archive</Link>
              </li>
              <li>
                <Link data-testid="footer-pricing" to="/pricing" className="hover:text-gold-400 transition-colors">Pricing Plans</Link>
              </li>
              <li>
                <Link data-testid="footer-faq" to="/faq" className="hover:text-gold-400 transition-colors">FAQ</Link>
              </li>
              <li>
                <Link data-testid="footer-contact" to="/contact" className="hover:text-gold-400 transition-colors">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Legal + Contact */}
          <div>
            <h4 className="text-white font-display font-semibold text-sm uppercase tracking-widest mb-5">
              Legal
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link data-testid="footer-legal-home" to="/" className="hover:text-gold-400 transition-colors">Home</Link>
              </li>
              <li>
                <Link data-testid="footer-disclaimer" to="/disclaimer" className="hover:text-gold-400 transition-colors">Disclaimer</Link>
              </li>
              <li>
                <Link data-testid="footer-terms" to="/terms" className="hover:text-gold-400 transition-colors">Terms and Conditions</Link>
              </li>
              <li>
                <Link data-testid="footer-privacy" to="/privacy" className="hover:text-gold-400 transition-colors">Privacy Policy</Link>
              </li>
            </ul>

            <div className="mt-8 flex items-start gap-2 text-sm">
              <Mail className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
              <a
                href="mailto:asianhpicks@gmail.com"
                data-testid="footer-email"
                className="hover:text-gold-400 transition-colors break-all"
              >
                asianhpicks@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Responsible gambling */}
        <div
          data-testid="responsible-gambling"
          className="mt-14 border border-red-900/40 bg-red-950/20 text-red-200/90 p-5 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-red-400" />
            <p className="text-xs sm:text-sm leading-relaxed">
              <span className="font-semibold text-red-200">Responsible Gambling — 18+.</span>{" "}
              Sports betting involves financial risk and can be addictive.
              Never wager more than you can afford to lose. Past results do not
              guarantee future performance. If you feel your gambling is
              becoming a problem, please seek help at{" "}
              <a
                href="https://www.begambleaware.org"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                BeGambleAware.org
              </a>{" "}
              or{" "}
              <a
                href="https://www.gamblingtherapy.org"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                GamblingTherapy.org
              </a>
              .
            </p>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-white/50">
          <p data-testid="footer-copyright">© 2023 Asian Picks. All rights reserved.</p>
          <p className="font-mono uppercase tracking-widest">asian-picks.com</p>
        </div>
      </div>
    </footer>
  );
}
